// Persistent meta-progression and settings (localStorage, failure-tolerant).

import type { MechId, Settings } from './types';
import { bus } from './events';

export interface SaveData {
  version: 1;
  /** Meta currency earned per run, spent in the hangar. */
  cores: number;
  unlocked: string[];
  runs: number;
  wins: number;
  /** Highest sector index reached (0-based). */
  bestSector: number;
  totalKills: number;
  bossKills: Partial<Record<string, number>>;
  /** Selected cosmetic id per mech. */
  cosmetics: Partial<Record<MechId, string>>;
  /** Challenge options toggled on for the next run. */
  challenges: string[];
  lastMech: MechId;
  seenTutorial: boolean;
}

export const DEFAULT_SETTINGS: Settings = {
  masterVolume: 0.8,
  musicVolume: 0.55,
  sfxVolume: 0.85,
  screenShake: 1,
  reducedFlashing: false,
  damageNumbers: true,
  showFps: false,
  deadzone: 0.2,
};

const DEFAULT_SAVE: SaveData = {
  version: 1,
  cores: 0,
  unlocked: ['mech:volt'],
  runs: 0,
  wins: 0,
  bestSector: 0,
  totalKills: 0,
  bossKills: {},
  cosmetics: {},
  challenges: [],
  lastMech: 'volt',
  seenTutorial: false,
};

const SAVE_KEY = 'neoncore.save.v1';
const SETTINGS_KEY = 'neoncore.settings.v1';

function readJson<T>(key: string): Partial<T> | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as Partial<T>) : null;
  } catch {
    return null;
  }
}

function writeJson(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Private mode / blocked storage: progression simply is not persisted.
  }
}

function urlFlag(name: string): boolean {
  try {
    return new URLSearchParams(window.location.search).has(name);
  } catch {
    return false;
  }
}

class SaveStore {
  data: SaveData = structuredClone(DEFAULT_SAVE);
  settings: Settings = { ...DEFAULT_SETTINGS };
  /** ?unlockall in the URL unlocks everything (testing / showcase). */
  readonly unlockAll = urlFlag('unlockall');

  load(): void {
    const d = readJson<SaveData>(SAVE_KEY);
    this.data = { ...structuredClone(DEFAULT_SAVE), ...(d ?? {}) } as SaveData;
    if (!this.data.unlocked.includes('mech:volt')) this.data.unlocked.push('mech:volt');
    const s = readJson<Settings>(SETTINGS_KEY);
    this.settings = { ...DEFAULT_SETTINGS, ...(s ?? {}) };
  }

  persist(): void {
    writeJson(SAVE_KEY, this.data);
  }

  persistSettings(): void {
    writeJson(SETTINGS_KEY, this.settings);
  }

  /** Items/mechs without an unlock id are always available. */
  isUnlocked(id: string | undefined): boolean {
    if (!id) return true;
    return this.unlockAll || this.data.unlocked.includes(id);
  }

  unlock(id: string): boolean {
    if (this.data.unlocked.includes(id)) return false;
    this.data.unlocked.push(id);
    this.persist();
    return true;
  }

  addCores(n: number): void {
    this.data.cores = Math.max(0, this.data.cores + Math.round(n));
    this.persist();
  }

  spendCores(n: number): boolean {
    if (this.data.cores < n) return false;
    this.data.cores -= n;
    this.persist();
    return true;
  }

  updateSettings(patch: Partial<Settings>): void {
    this.settings = { ...this.settings, ...patch };
    this.persistSettings();
    bus.emit('settings:changed', {});
  }

  /** Wipe progression (settings kept). */
  reset(): void {
    this.data = structuredClone(DEFAULT_SAVE);
    this.persist();
  }
}

export const save = new SaveStore();
